import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/components/Modal';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { inputStyle } from '@/lib/format';

import { CASE_KINDS, caseTarget, kindOf, useSafeCases, type SafeCase } from './caseApi';
import { LegacyRecordModal } from './LegacyArchivePage';
import { SOURCE_LABELS } from './legacyApi';

/**
 * 사고 · 시정조치 사례 모음(DOC-136 · 사용자 결정 2026-10-28) — 옛 보관함과 새 기록의 사고 · 아차사고 · 부적합/시정조치 · 점검 지적.
 *
 * <p>전체 화면(/cases)과 현장 화면(/sites/:id/cases). 현장 화면은 「전체 · 이 현장」 을 고른다.
 * 배정 현장 밖의 사례는 서버가 현장을 가려 보내고, 그 줄은 원본 대신 사례 창을 연다.
 */
export function CaseLibraryPage({ siteId }: { siteId?: string }) {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [onlySite, setOnlySite] = useState(false);
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [legacy, setLegacy] = useState<{ source: string; code: string } | null>(null);
  const [shown, setShown] = useState<SafeCase | null>(null);

  // 검색어는 치는 동안 멈춘 뒤에 묻는다.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const cases = useSafeCases({ q, type, siteId: siteId && onlySite ? siteId : null, from, to, page });
  const data = cases.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;
  const pick = (next: () => void) => {
    next();
    setPage(1);
  };

  const open = (row: SafeCase) => {
    const target = caseTarget(row);
    if (!target) setShown(row);
    else if ('legacy' in target) setLegacy(target.legacy);
    else navigate(target.path);
  };

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <PageHeader title="조치 사례" />

      <div className="flex flex-wrap items-center gap-2">
        {siteId && (
          <div className="cl-chips">
            <button type="button" className={!onlySite ? 'on' : ''} onClick={() => pick(() => setOnlySite(false))}>
              전체
            </button>
            <button type="button" className={onlySite ? 'on' : ''} onClick={() => pick(() => setOnlySite(true))}>
              이 현장
            </button>
          </div>
        )}
        <div className="cl-chips">
          <button type="button" className={type === '' ? 'on' : ''} onClick={() => pick(() => setType(''))}>
            전체
          </button>
          {CASE_KINDS.map((one) => (
            <button key={one.kind} type="button" className={type === one.kind ? 'on' : ''} onClick={() => pick(() => setType(one.kind))}>
              {one.label}
            </button>
          ))}
        </div>
        <input className="input" style={{ ...inputStyle, width: '220px' }} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="검색" aria-label="검색" />
        <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={from} onChange={(e) => pick(() => setFrom(e.target.value))} aria-label="시작일" />
        <span style={{ color: 'var(--text-muted)' }}>~</span>
        <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={to} onChange={(e) => pick(() => setTo(e.target.value))} aria-label="종료일" />
      </div>

      {cases.isPending ? (
        <LoadingBlock />
      ) : cases.isError ? (
        <LoadingBlock />
      ) : !data || data.rows.length === 0 ? (
        <EmptyCard>사례가 없습니다.</EmptyCard>
      ) : (
        <>
          <div className="cl-grid">
            {data.rows.map((row) => (
              <CaseCard key={row.key} row={row} showSite={!(siteId && onlySite)} onOpen={() => open(row)} />
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 text-ui-sm">
            <span style={{ color: 'var(--text-secondary)' }}>
              {data.total.toLocaleString()}건 · {page}/{pages}
            </span>
            <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              이전
            </button>
            <button type="button" className="btn-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              다음
            </button>
          </div>
        </>
      )}

      {legacy && <LegacyRecordModal source={legacy.source} code={legacy.code} onClose={() => setLegacy(null)} />}
      {shown && <CaseModal row={shown} onClose={() => setShown(null)} />}
    </div>
  );
}

/** 사례 한 장 — 머리(일자 · 구분 · 재해형태 · 공종 · 현장), 무엇이 · 왜 · 조치 세 줄. */
function CaseCard({ row, showSite, onOpen }: { row: SafeCase; showSite: boolean; onOpen: () => void }) {
  const kind = kindOf(row.kind);
  return (
    <button type="button" className="card cl-card" onClick={onOpen}>
      <div className="cl-head">
        <span className={`badge ${kind.tone}`}>{kind.label}</span>
        {row.category && <span className="cl-tag">{row.category}</span>}
        {row.trade && <span className="cl-tag">{row.trade}</span>}
        <span className="cl-date">{row.date ?? '—'}</span>
      </div>
      <CaseLines row={row} clamp />
      <div className="cl-foot">
        {showSite && row.siteName && <span>{row.siteName}</span>}
        {row.place && <span>{row.place}</span>}
        <span className="cl-src">{sourceLabel(row)}</span>
      </div>
    </button>
  );
}

function CaseLines({ row, clamp = false }: { row: SafeCase; clamp?: boolean }) {
  const lines: Array<[string, string | null]> = [
    ['무엇이', row.what],
    ['원인', row.cause],
    ['조치', row.action],
  ];
  return (
    <dl className="cl-lines">
      {lines
        .filter(([, value]) => value && value.trim())
        .map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className={clamp ? 'cl-clamp' : ''}>{value}</dd>
          </div>
        ))}
    </dl>
  );
}

/** 가린 사례(남의 현장) 창 — 원본을 열 수 없어 사례 글만. */
function CaseModal({ row, onClose }: { row: SafeCase; onClose: () => void }) {
  const kind = kindOf(row.kind);
  return (
    <Modal title={`${kind.label} · ${row.date ?? ''}`} onClose={onClose} maxWidth="720px">
      <div className="space-y-3">
        <style>{STYLES}</style>
        <div className="cl-head">
          {row.category && <span className="cl-tag">{row.category}</span>}
          {row.trade && <span className="cl-tag">{row.trade}</span>}
          {row.place && <span className="cl-tag">{row.place}</span>}
        </div>
        <CaseLines row={row} />
      </div>
    </Modal>
  );
}

function sourceLabel(row: SafeCase): string {
  if (row.origin === 'old') return row.refType ? `기존 ${SOURCE_LABELS[row.refType] ?? row.refType}` : '기존 데이터';
  return '';
}

const STYLES = `
.cl-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; padding: 4px; border-radius: 10px; background: var(--bg-hover, #f1f5f9); }
.cl-chips button { padding: 6px 10px; border: none; border-radius: 8px; background: transparent; font-size: 13px; cursor: pointer; }
.cl-chips button.on { background: var(--bg-card, #fff); font-weight: 700; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.cl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 12px; }
.cl-card { display: flex; flex-direction: column; gap: 8px; padding: 14px 16px; text-align: left; cursor: pointer; border: 1px solid var(--border); font: inherit; color: inherit; }
.cl-card:hover { border-color: var(--accent); }
.cl-head { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; }
.cl-tag { padding: 2px 8px; border-radius: 999px; background: var(--bg-hover, #f1f5f9); color: var(--text-secondary); }
.cl-date { margin-left: auto; color: var(--text-muted); }
.cl-lines { display: grid; gap: 4px; margin: 0; font-size: 13px; }
.cl-lines > div { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 8px; }
.cl-lines dt { color: var(--text-muted); font-weight: 600; }
.cl-lines dd { margin: 0; white-space: pre-wrap; word-break: break-word; }
.cl-clamp { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.cl-foot { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: auto; font-size: 12px; color: var(--text-secondary); }
.cl-src { margin-left: auto; color: var(--text-muted); }
`;
